import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Typography, Select, Card, Table, Spin, message } from 'antd';
import { MapContainer, TileLayer, Marker, Popup, CircleMarker, useMap } from 'react-leaflet';
import L from 'leaflet';
import axios from 'axios';
import { isLoggedIn } from '../../services/AuthService';
import './Reports.css';

const { Title } = Typography;
const { Option } = Select;

// Enterprise type colors (consistent with DashboardMap)
const ENTERPRISE_COLORS = {
    FARM: '#52c41a',
    TELECOMMUNICATION: '#1890ff',
    ELECTRICAL: '#faad14',
    TRANSPORT: '#722ed1',
    ORDINARY_BUILDING: '#eb2f96',
    NON_ORDINARY_BUILDING: '#f759ab'
};

const createIcon = (enterpriseType) => {
    const color = ENTERPRISE_COLORS[enterpriseType] || '#666';
    return L.divIcon({
        html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 4px rgba(0,0,0,0.3);"></div>`,
        className: 'custom-marker-icon',
        iconSize: [16, 16],
        iconAnchor: [8, 8]
    });
};

// Extract a [lng, lat] point from any GeoJSON geometry (centroid for non-Point types)
const getPointFromGeometry = (geo) => {
    if (!geo) return null;
    if (geo.type === 'Point') return geo.coordinates;
    if (geo.type === 'LineString' && geo.coordinates && geo.coordinates.length > 0) {
        let totalLng = 0, totalLat = 0;
        for (const coord of geo.coordinates) {
            totalLng += coord[0];
            totalLat += coord[1];
        }
        return [totalLng / geo.coordinates.length, totalLat / geo.coordinates.length];
    }
    if (geo.type === 'Polygon' && geo.coordinates && geo.coordinates.length > 0) {
        const ring = geo.coordinates[0];
        let totalLng = 0, totalLat = 0;
        for (const coord of ring) {
            totalLng += coord[0];
            totalLat += coord[1];
        }
        return [totalLng / ring.length, totalLat / ring.length];
    }
    return null;
};

// Component to control map panning/zooming when damages data changes
const MapController = ({ damagesWithGeometry, parseGeometry }) => {
    const map = useMap();

    useEffect(() => {
        if (damagesWithGeometry.length === 0) return;

        // Calculate bounds from all damage points
        const points = damagesWithGeometry
            .map(d => getPointFromGeometry(parseGeometry(d.geometry)))
            .filter(coords => coords !== null)
            .map(coords => [coords[1], coords[0]]);

        if (points.length > 0) {
            const bounds = L.latLngBounds(points);
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 14 });
        }
    }, [damagesWithGeometry, map, parseGeometry]);

    return null;
};

const DamageQuery = () => {
    const { t } = useTranslation();
    const [events, setEvents] = useState([]);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [selectedEnterpriseType, setSelectedEnterpriseType] = useState(null);
    const [loading, setLoading] = useState(false);
    const [eventsLoading, setEventsLoading] = useState(false);
    const [damages, setDamages] = useState([]);
    const [summary, setSummary] = useState(null);

    // Load events on mount
    useEffect(() => {
        const loadEvents = async () => {
            if (!isLoggedIn()) return;

            setEventsLoading(true);
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_ENDPOINT}/events/`,
                    { params: { size: 100, sort: 'date,desc' } }
                );
                setEvents(response.data.content || []);
            } catch (error) {
                console.error('Error loading events:', error);
                message.error('Failed to load events');
            } finally {
                setEventsLoading(false);
            }
        };
        loadEvents();
    }, []);

    // Auto-load damages when filters change
    useEffect(() => {
        const loadDamages = async () => {
            if (!selectedEvent) {
                setDamages([]);
                setSummary(null);
                return;
            }

            setLoading(true);
            try {
                const params = { eventId: selectedEvent };
                if (selectedEnterpriseType) {
                    params.enterpriseType = selectedEnterpriseType;
                }

                // Fetch damages and summary in parallel
                const [damagesResponse, summaryResponse] = await Promise.all([
                    axios.get(`${process.env.REACT_APP_API_ENDPOINT}/reports/damages`, { params }),
                    axios.get(`${process.env.REACT_APP_API_ENDPOINT}/reports/damages/summary`, { params })
                ]);

                setDamages(damagesResponse.data || []);
                setSummary(summaryResponse.data);
            } catch (error) {
                console.error('Error fetching damages:', error);
                message.error('Failed to load damage data');
            } finally {
                setLoading(false);
            }
        };
        loadDamages();
    }, [selectedEvent, selectedEnterpriseType]);

    // Format currency
    const formatCurrency = (value) => {
        if (value == null) return '-';
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'EUR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(value);
    };

    // Parse geometry for map (memoized for stable reference)
    const parseGeometry = useCallback((geometryString) => {
        try {
            return JSON.parse(geometryString);
        } catch (e) {
            return null;
        }
    }, []);

    // Get damages with valid geometry for map
    const damagesWithGeometry = damages.filter(d => d.geometry);

    // Calculate map center
    const getMapCenter = () => {
        if (damagesWithGeometry.length === 0) {
            return [45.4642, 9.1900]; // Default: Milan
        }

        let totalLat = 0, totalLng = 0, count = 0;
        damagesWithGeometry.forEach(d => {
            const coords = getPointFromGeometry(parseGeometry(d.geometry));
            if (coords) {
                totalLat += coords[1];
                totalLng += coords[0];
                count++;
            }
        });

        if (count > 0) {
            return [totalLat / count, totalLng / count];
        }
        return [45.4642, 9.1900];
    };

    // Table columns
    const columns = [
        {
            title: 'Enterprise',
            dataIndex: 'enterpriseName',
            key: 'enterpriseName',
            ellipsis: true,
            width: 200
        },
        {
            title: 'Type',
            dataIndex: 'enterpriseType',
            key: 'enterpriseType',
            width: 120,
            render: (type) => (
                <span style={{ color: ENTERPRISE_COLORS[type] || '#666' }}>
                    {type}
                </span>
            )
        },
        {
            title: 'Description',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true
        },
        {
            title: 'Location',
            key: 'location',
            width: 150,
            render: (_, record) => record.town || record.county || '-'
        },
        {
            title: 'Est. Cost',
            dataIndex: 'totalEstimatedCost',
            key: 'totalEstimatedCost',
            width: 120,
            align: 'right',
            render: formatCurrency
        },
        {
            title: 'Appr. Cost',
            dataIndex: 'totalApprovedCost',
            key: 'totalApprovedCost',
            width: 120,
            align: 'right',
            render: formatCurrency
        }
    ];

    return (
        <div className="reports-container">
            <Title level={2} className="reports-title">Damage Query</Title>

            {/* Filters */}
            <div className="reports-filters">
                <Select
                    placeholder="Select an event"
                    style={{ minWidth: 300 }}
                    loading={eventsLoading}
                    value={selectedEvent}
                    onChange={setSelectedEvent}
                    showSearch
                    optionFilterProp="children"
                    allowClear
                >
                    {events.map(event => (
                        <Option key={event.id} value={event.id}>
                            {event.name} ({event.type})
                        </Option>
                    ))}
                </Select>

                <Select
                    placeholder="Enterprise type (optional)"
                    style={{ minWidth: 200 }}
                    value={selectedEnterpriseType}
                    onChange={setSelectedEnterpriseType}
                    allowClear
                >
                    <Option value="ORDINARY_BUILDING">Building</Option>
                    <Option value="NON_ORDINARY_BUILDING">Non-ordinary building</Option>
                    <Option value="TRANSPORT">Transport</Option>
                    <Option value="FARM">Farm</Option>
                    <Option value="TELECOMMUNICATION">Telecommunication</Option>
                    <Option value="ELECTRICAL">Electrical</Option>
                </Select>
            </div>

            {/* Summary Cards */}
            {summary && (
                <div className="summary-cards">
                    <Card className="summary-card">
                        <div className="summary-value">{summary.totalDamages || 0}</div>
                        <div className="summary-label">Total Damages</div>
                    </Card>
                    <Card className="summary-card">
                        <div className="summary-value">{formatCurrency(summary.totalEstimatedCost)}</div>
                        <div className="summary-label">Estimated Cost</div>
                    </Card>
                    <Card className="summary-card">
                        <div className="summary-value">{formatCurrency(summary.totalApprovedCost)}</div>
                        <div className="summary-label">Approved Cost</div>
                    </Card>
                </div>
            )}

            {/* Map */}
            {damagesWithGeometry.length > 0 && (
                <div className="report-map-container">
                    <MapContainer
                        center={getMapCenter()}
                        zoom={10}
                        style={{ height: '100%', width: '100%' }}
                    >
                        <MapController
                            damagesWithGeometry={damagesWithGeometry}
                            parseGeometry={parseGeometry}
                        />
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        {damagesWithGeometry.map(damage => {
                            const coords = getPointFromGeometry(parseGeometry(damage.geometry));
                            if (!coords) return null;

                            return (
                                <CircleMarker
                                    key={damage.damageId}
                                    center={[coords[1], coords[0]]}
                                    radius={8}
                                    pathOptions={{
                                        fillColor: ENTERPRISE_COLORS[damage.enterpriseType] || '#666',
                                        color: 'white',
                                        weight: 2,
                                        fillOpacity: 0.8
                                    }}
                                >
                                    <Popup>
                                        <div>
                                            <strong>{damage.enterpriseName}</strong>
                                            <br />
                                            <span style={{ color: '#888' }}>{damage.enterpriseType}</span>
                                            <br />
                                            {damage.description && (
                                                <>
                                                    <span>{damage.description}</span>
                                                    <br />
                                                </>
                                            )}
                                            <strong>Est. Cost:</strong> {formatCurrency(damage.totalEstimatedCost)}
                                        </div>
                                    </Popup>
                                </CircleMarker>
                            );
                        })}
                    </MapContainer>
                </div>
            )}

            {/* Loading indicator */}
            {loading && (
                <div style={{ textAlign: 'center', padding: '48px' }}>
                    <Spin size="large" />
                </div>
            )}

            {/* Damages Table */}
            {!loading && damages.length > 0 && (
                <div className="damages-table">
                    <Table
                        dataSource={damages}
                        columns={columns}
                        rowKey="damageId"
                        pagination={{ pageSize: 10 }}
                        scroll={{ x: 800 }}
                    />
                </div>
            )}

            {/* No data message */}
            {!loading && selectedEvent && damages.length === 0 && (
                <div className="no-data-message">
                    No damages found for the selected criteria.
                </div>
            )}
        </div>
    );
};

export default DamageQuery;
